import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SpAccount } from '../SpAccountContext';
import { API_BASE, downloadExport } from '../api';

interface Props {
  account: SpAccount;
  marketplaceId: number | null;
}

interface Order {
  id: string; amazon_order_id: string; marketplace_id: number; status: string;
  fulfillment_channel: string | null; purchase_date: string | null;
  order_total_amount: number | null; order_total_currency: string | null;
  number_of_items_shipped: number | null; number_of_items_unshipped: number | null;
  items_count: number;
  ship_country: string | null;
}

interface OrderItem {
  order_item_id: string; asin: string | null; seller_sku: string | null; title: string | null;
  quantity_ordered: number | null; quantity_shipped: number | null;
  item_price_amount: number | null; item_price_currency: string | null;
  item_tax_amount: number | null; promotion_discount_amount: number | null;
}

const STATUS_COLORS: Record<string, string> = {
  Shipped: 'var(--success)', Shipping: 'var(--success)',
  Unshipped: 'var(--warning)', PartiallyShipped: 'var(--warning)',
  Pending: 'var(--info)',
  Cancelled: 'var(--danger)', Canceled: 'var(--danger)',
  PendingAvailability: 'var(--text-muted)',
};

interface JobProgress {
  status: string;          // running | completed | failed | cancelled
  phase?: string;          // starting | creating_report | polling_report | downloading | saving | ...
  message?: string | null;
  records_fetched: number;
  chunks_total?: number;
  chunks_done?: number;
  marketplaces_total?: number;
  marketplaces_done?: number;
  current_marketplace?: string | null;
  cancel_requested?: boolean;
  error?: string | null;
  error_type?: string | null;
}

// Ordered phases for the orders report pipeline → label + rough weight for the bar.
const PHASE_LABEL: Record<string, string> = {
  starting: 'Starting',
  creating_report: 'Requesting report',
  polling_report: 'Amazon is generating the report',
  downloading: 'Downloading',
  saving: 'Saving orders',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const SpOrders: React.FC<Props> = ({ account, marketplaceId }) => {
  const token = localStorage.getItem('access_token') ?? '';
  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<OrderItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterPreset, setFilterPreset] = useState('');
  const [activePreset, setActivePreset] = useState<string | null>('7d');
  const [pullDateFrom, setPullDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [pullDateTo, setPullDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [pullReportType, setPullReportType] = useState('GET_XML_ALL_ORDERS_DATA_BY_ORDER_DATE_GENERAL');
  const [pulling, setPulling] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [jobProgress, setJobProgress] = useState<JobProgress | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const jobIdRef = useRef<string | null>(null);

  const applyPreset = (label: string, days: number) => {
    const to = new Date();
    const from = new Date(); from.setDate(from.getDate() - days);
    setPullDateFrom(from.toISOString().split('T')[0]);
    setPullDateTo(to.toISOString().split('T')[0]);
    setActivePreset(label);
  };
  const PAGE_SIZE = 50;

  const headers = { Authorization: `Bearer ${token}` };

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const loadOrders = useCallback(async (
    p = 1,
    overrides?: Partial<{ search: string; statusFilter: string; dateFrom: string; dateTo: string; sortKey: string | null; sortDir: 'asc' | 'desc' }>,
  ) => {
    setLoading(true);
    const s  = (overrides?.search      ?? search).trim();
    const st = overrides?.statusFilter ?? statusFilter;
    const df = overrides?.dateFrom      ?? dateFrom;
    const dt = overrides?.dateTo        ?? dateTo;
    const sk = overrides?.sortKey !== undefined ? overrides.sortKey : sortKey;
    const sd = overrides?.sortDir      ?? sortDir;
    const params = new URLSearchParams({ page: String(p), page_size: String(PAGE_SIZE) });
    if (marketplaceId) params.set('marketplace_id', String(marketplaceId));
    if (s) params.set('search', s);
    if (st) params.set('status', st);
    if (df) params.set('date_from', new Date(df).toISOString());
    if (dt) params.set('date_to', new Date(dt + 'T23:59:59').toISOString());
    if (sk) { params.set('sort_key', sk); params.set('sort_dir', sd); }
    try {
      const res = await fetch(`${API_BASE}/api/v1/sp-api/accounts/${account.seller_id}/orders?${params}`, { headers });
      const data = await res.json();
      setOrders(data.data ?? []);
      setTotal(data.total ?? 0);
      setPage(p);
    } catch {
      // network error — keep existing data, stop spinner
    } finally { setLoading(false); }
  }, [account.seller_id, marketplaceId, search, statusFilter, dateFrom, dateTo, sortKey, sortDir, token]);

  useEffect(() => { loadOrders(1); }, [account.seller_id, marketplaceId]);

  // View-filter date presets. Sets both date inputs and applies immediately.
  // "All time" clears the range. Dates use local-day boundaries formatted as
  // YYYY-MM-DD to match the <input type="date"> values.
  const applyFilterPreset = (preset: string) => {
    const fmt = (d: Date) => d.toISOString().split('T')[0];
    const today = new Date();
    const from = new Date(today);
    let df = '';
    let dt = fmt(today);
    switch (preset) {
      case 'today':                                              break;
      case 'yesterday': from.setDate(today.getDate() - 1); dt = fmt(from); break;
      case '7d':        from.setDate(today.getDate() - 6);  break;
      case '30d':       from.setDate(today.getDate() - 29); break;
      case '3m':        from.setMonth(today.getMonth() - 3); break;
      case '1y':        from.setFullYear(today.getFullYear() - 1); break;
      case 'all':       setFilterPreset('all'); setDateFrom(''); setDateTo('');
                        loadOrders(1, { dateFrom: '', dateTo: '' }); return;
      default:          return; // "Custom range" — leave inputs as-is
    }
    df = preset === 'today' ? fmt(today) : fmt(from);
    setFilterPreset(preset);
    setDateFrom(df);
    setDateTo(dt);
    loadOrders(1, { dateFrom: df, dateTo: dt });
  };

  const loadItems = async (orderId: string, amazonOrderId: string) => {
    if (expandedId === amazonOrderId) { setExpandedId(null); return; }
    setExpandedId(amazonOrderId);
    setLoadingItems(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/sp-api/accounts/${account.seller_id}/orders/${amazonOrderId}`, { headers });
      const data = await res.json();
      setExpandedItems(data.items ?? []);
    } catch {
      setExpandedItems([]);
    } finally { setLoadingItems(false); }
  };

  const startPull = async () => {
    if (pulling) return;
    setPulling(true);
    setCancelling(false);
    setJobProgress({ status: 'running', phase: 'starting', message: 'Starting pull…', records_fetched: 0 });
    try {
      const mkIds = marketplaceId ? [marketplaceId] : account.marketplaces.map(m => m.id);
      if (mkIds.length === 0) {
        setJobProgress({ status: 'failed', records_fetched: 0, error_type: 'No marketplaces',
          error: 'This account has no marketplaces selected. Add one under Amazon Accounts first.' });
        setPulling(false);
        return;
      }
      const res = await fetch(`${API_BASE}/api/v1/sp-api/accounts/${account.seller_id}/pull/orders`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          marketplace_ids: mkIds,
          date_from: new Date(pullDateFrom + 'T00:00:00Z').toISOString(),
          date_to: new Date(pullDateTo + 'T23:59:59Z').toISOString(),
          report_type: pullReportType || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setJobProgress({ status: 'failed', records_fetched: 0, error_type: `HTTP ${res.status}`,
          error: data.detail || 'The server rejected the pull request.' });
        setPulling(false);
        return;
      }
      if (data.job_id) {
        jobIdRef.current = data.job_id;
        pollRef.current = setInterval(() => pollJob(data.job_id), 1500);
      } else {
        setJobProgress({ status: 'failed', records_fetched: 0, error: 'Server did not return a job id.' });
        setPulling(false);
      }
    } catch {
      setJobProgress({ status: 'failed', records_fetched: 0, error_type: 'Network',
        error: 'Could not reach the backend. Check your connection and try again.' });
      setPulling(false);
    }
  };

  const cancelPull = async () => {
    const jid = jobIdRef.current;
    if (!jid) return;
    setCancelling(true);
    try {
      await fetch(`${API_BASE}/api/v1/sp-api/jobs/${jid}/cancel`, { method: 'POST', headers });
      // The worker reports `cancelled` once it stops; keep polling until then.
    } catch {
      setCancelling(false);
    }
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const pollJob = async (jid: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/sp-api/jobs/${jid}`, { headers });
      if (!res.ok) {
        // Job store may have evicted it; surface rather than spin forever.
        if (res.status === 404) {
          stopPolling();
          setPulling(false);
          setCancelling(false);
          setJobProgress(p => p && p.status === 'running'
            ? { ...p, status: 'failed', error: 'Lost track of the job (it may have expired). Reload and retry.' }
            : p);
        }
        return;
      }
      const data: JobProgress = await res.json();
      setJobProgress(data);
      if (data.status !== 'running') {
        stopPolling();
        setPulling(false);
        setCancelling(false);
        jobIdRef.current = null;
        if (data.status === 'completed' || data.status === 'cancelled') loadOrders(1);
      }
    } catch { /* transient network error — keep polling */ }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const progressPct = (() => {
    if (!jobProgress) return 0;
    if (jobProgress.status === 'completed') return 100;
    const total = jobProgress.chunks_total ?? 0;
    if (total > 0) return Math.min(99, Math.round(((jobProgress.chunks_done ?? 0) / total) * 100));
    return jobProgress.phase === 'starting' ? 4 : 12; // indeterminate-ish until chunk counts arrive
  })();

  const fmtCurrency = (amount: number | null, currency: string | null) => {
    if (amount == null) return '—';
    return `${currency ?? ''} ${amount.toFixed(2)}`;
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Server-side sort: toggling a header re-queries page 1 so the sort spans the
  // whole result set, not just the rows currently loaded.
  const handleSort = (key: string | null) => {
    if (!key) return;
    const nextDir: 'asc' | 'desc' = sortKey === key ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
    setSortKey(key);
    setSortDir(nextDir);
    loadOrders(1, { sortKey: key, sortDir: nextDir });
  };

  const sortIcon = (key: string | null) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ⇅';

  return (
    <div>
      {/* Stats + Pull bar — single combined card */}
      <div className="card" style={{ marginBottom: 10, padding: '8px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* Stat chips */}
          {[
            { label: 'Orders', value: total, accent: 'var(--accent)' },
            { label: 'Unshipped', value: orders.filter(o => o.status === 'Unshipped').length, accent: 'var(--warning)' },
            { label: 'FBA', value: orders.filter(o => o.fulfillment_channel === 'AFN').length, accent: 'var(--success)' },
            { label: 'MFN', value: orders.filter(o => o.fulfillment_channel === 'MFN').length, accent: 'var(--info)' },
          ].map(({ label, value, accent }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'baseline', gap: 5, paddingRight: 12, borderRight: '1px solid var(--border)' }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: accent, lineHeight: 1 }}>{value}</span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
            </div>
          ))}

          {/* Divider then pull controls */}
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap', paddingLeft: 2 }}>Pull:</span>
          <select className="form-input" style={{ width: 230, fontSize: 11, padding: '3px 6px' }} value={pullReportType}
            onChange={e => setPullReportType(e.target.value)}>
            <option value="GET_XML_ALL_ORDERS_DATA_BY_ORDER_DATE_GENERAL">By Order Date</option>
            <option value="GET_XML_ALL_ORDERS_DATA_BY_LAST_UPDATE_GENERAL">By Last Update</option>
          </select>
          <input type="date" className="form-input" style={{ width: 128, fontSize: 11, padding: '3px 6px' }} value={pullDateFrom}
            onChange={e => { setPullDateFrom(e.target.value); setActivePreset(null); }} />
          <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>→</span>
          <input type="date" className="form-input" style={{ width: 128, fontSize: 11, padding: '3px 6px' }} value={pullDateTo}
            onChange={e => { setPullDateTo(e.target.value); setActivePreset(null); }} />
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Quick:</span>
          {[['2h', 0.08], ['24h', 1], ['7d', 7], ['30d', 30], ['60d', 60], ['90d', 90]].map(([label, days]) => {
            const isActive = activePreset === label;
            return (
              <button
                key={label as string}
                className="btn"
                style={{
                  padding: '2px 7px', fontSize: 11,
                  background: isActive ? 'var(--accent)' : 'var(--bg-secondary)',
                  color: isActive ? '#fff' : 'var(--text-muted)',
                  border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
                  fontWeight: isActive ? 600 : 400,
                }}
                onClick={() => applyPreset(label as string, days as number)}
              >{label}</button>
            );
          })}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: 12 }}
              onClick={() => downloadExport('/api/v1/sp-api/export/orders.csv', 'orders.csv')}>
              ⬇ Export All
            </button>
            {pulling ? (
              <button className="btn" onClick={cancelPull} disabled={cancelling}
                style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-border)', color: 'var(--danger)', opacity: cancelling ? 0.7 : 1, padding: '4px 10px', fontSize: 11 }}>
                {cancelling ? 'Stopping…' : '■ Stop'}
              </button>
            ) : (
              <button className="btn btn-primary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={startPull}>↓ Pull Now</button>
            )}
          </div>
        </div>

          {/* Live progress while running */}
          {jobProgress && (jobProgress.status === 'running') && (
            <div style={{
              marginTop: 4, padding: '12px 14px', borderRadius: 8,
              background: 'var(--bg-secondary)', border: '1px solid var(--border)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span className="spinner" style={{ width: 14, height: 14, flexShrink: 0 }} />
                  <span style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
                    {PHASE_LABEL[jobProgress.phase ?? ''] ?? 'Working'}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {jobProgress.message}
                  </span>
                </div>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {(jobProgress.chunks_total ?? 0) > 0 && `${jobProgress.chunks_done ?? 0}/${jobProgress.chunks_total} reports · `}
                  {jobProgress.records_fetched} orders
                </span>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${progressPct}%`, borderRadius: 3,
                  background: cancelling ? 'var(--warning)' : 'var(--accent)',
                  transition: 'width 0.4s ease',
                }} />
              </div>
              {cancelling && (
                <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--warning)' }}>
                  Stopping after the current step finishes — partial data will be discarded.
                </div>
              )}
            </div>
          )}

          {/* Success banner */}
          {jobProgress && jobProgress.status === 'completed' && (
            <div className="alert alert-success" style={{ marginTop: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <span>✓ Done — {jobProgress.records_fetched} orders synced.</span>
              <button className="btn btn-secondary btn-small" onClick={() => setJobProgress(null)}>Dismiss</button>
            </div>
          )}

          {/* Cancelled banner */}
          {jobProgress && jobProgress.status === 'cancelled' && (
            <div className="alert" style={{
              marginTop: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
              background: 'var(--warning-bg, rgba(234,179,8,0.12))', border: '1px solid var(--warning)', color: 'var(--warning)',
            }}>
              <span>■ Pull stopped. No partial data was saved.</span>
              <button className="btn btn-secondary btn-small" onClick={() => setJobProgress(null)}>Dismiss</button>
            </div>
          )}

          {/* Error panel */}
          {jobProgress && jobProgress.status === 'failed' && (
            <div className="alert alert-error" style={{ marginTop: 4, alignItems: 'flex-start', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, marginBottom: 2 }}>
                    ✗ Pull failed{jobProgress.error_type ? ` (${jobProgress.error_type})` : ''}
                  </div>
                  <div style={{ fontSize: 12.5, lineHeight: 1.5, wordBreak: 'break-word' }}>
                    {jobProgress.error || 'An unknown error occurred.'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-small" onClick={startPull}>↻ Retry</button>
                <button className="btn btn-secondary btn-small" onClick={() => setJobProgress(null)}>Dismiss</button>
              </div>
            </div>
          )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="form-input"
          style={{ flex: '1 1 200px', fontSize: 12, padding: '4px 8px' }}
          placeholder="Search order ID, ASIN, SKU, title, or country…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && loadOrders(1)}
        />
        <select className="form-input" style={{ width: 140, fontSize: 12, padding: '4px 8px' }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {/* Values match Amazon's XML OrderStatus strings stored in the DB
              (e.g. "Cancelled"/"Shipping", not "Canceled"/"Shipped"). */}
          {['Pending', 'Unshipped', 'PartiallyShipped', 'Shipped', 'Shipping', 'Cancelled'].map(s => <option key={s}>{s}</option>)}
        </select>
        <select className="form-input" style={{ width: 130, fontSize: 12, padding: '4px 8px' }} value={filterPreset} onChange={e => applyFilterPreset(e.target.value)}>
          <option value="">Custom range</option>
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="3m">Last 3 months</option>
          <option value="1y">Last year</option>
          <option value="all">All time</option>
        </select>
        <input type="date" className="form-input" style={{ width: 130, fontSize: 12, padding: '4px 8px' }} value={dateFrom} onChange={e => { setDateFrom(e.target.value); setFilterPreset(''); }} />
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>→</span>
        <input type="date" className="form-input" style={{ width: 130, fontSize: 12, padding: '4px 8px' }} value={dateTo} onChange={e => { setDateTo(e.target.value); setFilterPreset(''); }} />
        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => loadOrders(1)} disabled={loading}>Apply</button>
        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => { setSearch(''); setStatusFilter(''); setDateFrom(''); setDateTo(''); setFilterPreset(''); loadOrders(1, { search: '', statusFilter: '', dateFrom: '', dateTo: '' }); }}>Reset</button>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /> Loading orders…</div>
      ) : (
        <>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                  {([
                    { h: 'Order ID', key: 'amazon_order_id' },
                    { h: 'Date', key: 'purchase_date' },
                    { h: 'Status', key: 'status' },
                    { h: 'Channel', key: 'fulfillment_channel' },
                    { h: 'Items', key: 'items_count' },
                    { h: 'Total', key: 'order_total_amount' },
                    { h: 'Country', key: 'ship_country' },
                    { h: '', key: null },
                  ] as { h: string; key: string | null }[]).map(({ h, key }) => (
                    <th key={h || '__expand'} onClick={() => handleSort(key)}
                      style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: sortKey === key ? 'var(--accent)' : 'var(--text-muted)', cursor: key ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>
                      {h}{key ? <span style={{ opacity: sortKey === key ? 1 : 0.35, fontSize: 10 }}>{sortIcon(key)}</span> : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <React.Fragment key={order.id}>
                    <tr style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                      onClick={() => loadItems(order.id, order.amazon_order_id)}>
                      <td style={{ padding: '10px 14px' }}>
                        <code style={{ fontSize: 12 }}>{order.amazon_order_id}</code>
                      </td>
                      <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {order.purchase_date ? new Date(order.purchase_date).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          background: (STATUS_COLORS[order.status] ?? 'var(--text-muted)') + '22',
                          color: STATUS_COLORS[order.status] ?? 'var(--text-muted)',
                        }}>{order.status}</span>
                      </td>
                      <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{order.fulfillment_channel ?? '—'}</td>
                      <td style={{ padding: '10px 14px' }}>{order.items_count}</td>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>{fmtCurrency(order.order_total_amount, order.order_total_currency)}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{order.ship_country ?? '—'}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--accent)' }}>{expandedId === order.amazon_order_id ? '▲' : '▼'}</td>
                    </tr>
                    {expandedId === order.amazon_order_id && (
                      <tr>
                        <td colSpan={8} style={{ background: 'var(--bg-secondary)', padding: '12px 20px' }}>
                          {loadingItems ? <div className="loading"><div className="spinner" /> Loading items…</div> : (
                            <table style={{ width: '100%', fontSize: 12 }}>
                              <thead>
                                <tr style={{ color: 'var(--text-muted)' }}>
                                  {['ASIN', 'SKU', 'Title', 'Qty', 'Price', 'Tax', 'Discount'].map(h => (
                                    <th key={h} style={{ padding: '4px 8px', textAlign: 'left', fontWeight: 600 }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {expandedItems.map(item => (
                                  <tr key={item.order_item_id}>
                                    <td style={{ padding: '4px 8px' }}><code>{item.asin ?? '—'}</code></td>
                                    <td style={{ padding: '4px 8px' }}>{item.seller_sku ?? '—'}</td>
                                    <td style={{ padding: '4px 8px', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title ?? '—'}</td>
                                    <td style={{ padding: '4px 8px' }}>{item.quantity_ordered ?? 0}</td>
                                    <td style={{ padding: '4px 8px' }}>{fmtCurrency(item.item_price_amount, item.item_price_currency)}</td>
                                    <td style={{ padding: '4px 8px' }}>{fmtCurrency(item.item_tax_amount, item.item_price_currency)}</td>
                                    <td style={{ padding: '4px 8px' }}>{item.promotion_discount_amount ? `-${fmtCurrency(item.promotion_discount_amount, item.item_price_currency)}` : '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {orders.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>No orders found. Pull data to get started.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" disabled={page <= 1} onClick={() => loadOrders(page - 1)}>← Prev</button>
              <span style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 13 }}>Page {page} of {totalPages}</span>
              <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => loadOrders(page + 1)}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SpOrders;
